import {
    useState,
    useCallback,
    useRef,
    useEffect,
} from 'react';
import useManageTodos from 'data/hooks/useManageTodos';
import { ItemUser } from 'data/firestore/globals';
import { CornerDownLeft } from 'lucide-react';
import Plus from 'components/icons/plus';
import ChevronDown from 'components/icons/chevronDown';
import ChevronRight from 'components/icons/chevronRight';
import TeamMemberPicker from 'components/TeamMemberPicker';
import {
    CreateFormPrioritySelect,
    CreateFormTextarea,
    CreateFormButton,
    AddButton,
    QuickAddContainer,
    QuickAddRow,
    QuickAddInput,
    QuickAddHint,
    DetailsToggle,
    DetailsSection,
    DetailsFieldsRow,
    QuickAddActions,
    CreateAnotherLabel,
    StyledDateTimePicker,
    StyledAssigneeWrapper,
} from './styled';

interface TodoCreateFormProps {
    agentId: string;
    opportunityId: string;
    pipelineId: string;
    stageId: string;
    onTodoCreated?: () => void;
}

type TodoPriority = 'low' | 'medium' | 'high' | '';

const getDateInputValue = (dueDate?: Date | string | null) => {
    if (!dueDate) return '';
    const parsed = dueDate instanceof Date ? dueDate : new Date(dueDate);
    if (Number.isNaN(parsed.getTime())) return '';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getDueDateISO = (dueDate?: string) => {
    if (!dueDate) return undefined;
    const match = dueDate.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) return undefined;

    const [, year, month, day, hours, minutes] = match;
    const parsed = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hours),
        Number(minutes),
        0,
        0,
    );

    if (Number.isNaN(parsed.getTime())) return undefined;
    return parsed.toISOString();
};

export interface TodoFormProps {
    agentId: string;
    title: string;
    description: string;
    assignedTo: ItemUser | null;
    priority: TodoPriority;
    dueDate: string;
    isSubmitting?: boolean;
    submitLabel?: string;
    submittingLabel?: string;
    onTitleChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onAssignedToChange: (value: ItemUser | null) => void;
    onPriorityChange: (value: TodoPriority) => void;
    onDueDateChange: (value: string) => void;
    onSubmit: () => void;
    onCancel: () => void;
    autoFocus?: boolean;
}

export const TodoForm = ({
    agentId,
    title,
    description,
    assignedTo,
    priority,
    dueDate,
    isSubmitting = false,
    submitLabel = 'Create',
    submittingLabel = 'Creating...',
    onTitleChange,
    onDescriptionChange,
    onAssignedToChange,
    onPriorityChange,
    onDueDateChange,
    onSubmit,
    onCancel,
    autoFocus = true,
}: TodoFormProps) => {
    const titleInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (autoFocus && titleInputRef.current) {
            titleInputRef.current.focus();
        }
    }, [autoFocus]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
        }
    }, [onSubmit, onCancel]);

    return (
        <QuickAddContainer>
            <QuickAddRow>
                <QuickAddInput
                    ref={titleInputRef}
                    type="text"
                    placeholder="What needs to be done?"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSubmitting}
                />
            </QuickAddRow>
            <DetailsSection>
                <DetailsFieldsRow>
                    <CreateFormPrioritySelect
                        value={priority}
                        onChange={(e) => onPriorityChange(e.target.value as TodoPriority)}
                        onKeyDown={handleKeyDown}
                        aria-label="Priority"
                    >
                        <option value="">Priority</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                    </CreateFormPrioritySelect>
                    <StyledDateTimePicker
                        value={dueDate ? new Date(dueDate) : undefined}
                        onChange={(value: Date) => {
                            if (!value) {
                                onDueDateChange('');
                                return;
                            }
                            const year = value.getFullYear();
                            const month = String(value.getMonth() + 1).padStart(2, '0');
                            const day = String(value.getDate()).padStart(2, '0');
                            const hours = String(value.getHours()).padStart(2, '0');
                            const minutes = String(value.getMinutes()).padStart(2, '0');
                            onDueDateChange(`${year}-${month}-${day}T${hours}:${minutes}`);
                        }}
                        placeholder="Due date"
                        hideLabel
                        size="small"
                        borderRadius="semi-rounded"
                    />
                </DetailsFieldsRow>
                <StyledAssigneeWrapper>
                    <TeamMemberPicker
                        agentId={agentId}
                        value={assignedTo || undefined}
                        placeholder="Assign to..."
                        onChange={(value) => onAssignedToChange(value ?? null)}
                        hideLabel
                    />
                </StyledAssigneeWrapper>
                <CreateFormTextarea
                    placeholder="Add details (optional)"
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={2}
                />
            </DetailsSection>
            <QuickAddActions>
                <CreateFormButton
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </CreateFormButton>
                <CreateFormButton
                    variant="primary"
                    onClick={onSubmit}
                    disabled={!title.trim() || isSubmitting}
                >
                    {isSubmitting ? submittingLabel : submitLabel}
                </CreateFormButton>
            </QuickAddActions>
        </QuickAddContainer>
    );
};

const TodoCreateForm = ({
    agentId,
    opportunityId,
    pipelineId,
    stageId,
    onTodoCreated = () => {},
}: TodoCreateFormProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [createAnother, setCreateAnother] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [assignedTo, setAssignedTo] = useState<ItemUser | null>(null);
    const [priority, setPriority] = useState<TodoPriority>('');
    const [dueDate, setDueDate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const { createTodo } = useManageTodos({ agentId });

    useEffect(() => {
        if (isExpanded && titleInputRef.current) {
            titleInputRef.current.focus();
        }
    }, [isExpanded]);

    const handleExpand = useCallback(() => {
        setIsExpanded(true);
    }, []);

    const resetForm = useCallback(() => {
        setTitle('');
        setDescription('');
        setAssignedTo(null);
        setPriority('');
        setDueDate('');
    }, []);

    const handleCancel = useCallback(() => {
        setIsExpanded(false);
        setShowDetails(false);
        setCreateAnother(false);
        resetForm();
    }, [resetForm]);

    const handleSubmit = useCallback(async () => {
        if (!title.trim()) return;

        setIsSubmitting(true);
        try {
            await createTodo({
                type: 'opportunity',
                title: title.trim(),
                description: description.trim() || undefined,
                status: 'pending',
                priority: priority || undefined,
                opportunityId,
                pipelineId,
                stageId,
                assignedTo: assignedTo || undefined,
                dueDate: getDueDateISO(dueDate),
            });

            resetForm();
            onTodoCreated();

            if (!createAnother) {
                setIsExpanded(false);
                setShowDetails(false);
            } else {
                // Keep form open and focus back on title
                setTimeout(() => titleInputRef.current?.focus(), 0);
            }
        } catch (error) {
            // Error is handled by useManageTodos
        } finally {
            setIsSubmitting(false);
        }
    }, [
        title,
        description,
        assignedTo,
        priority,
        dueDate,
        createTodo,
        createAnother,
        opportunityId,
        pipelineId,
        stageId,
        onTodoCreated,
        resetForm,
    ]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (title.trim() && !isSubmitting) {
                handleSubmit();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        } else if (e.key === 'Tab' && !e.shiftKey && !showDetails) {
            e.preventDefault();
            setShowDetails(true);
        }
    }, [title, isSubmitting, showDetails, handleSubmit, handleCancel]);

    const handleDetailsKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (title.trim() && !isSubmitting) {
                handleSubmit();
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    }, [title, isSubmitting, handleSubmit, handleCancel]);

    const hasDetails = Boolean(priority || dueDate || assignedTo || description.trim());

    if (!isExpanded) {
        return (
            <AddButton onClick={handleExpand}>
                <Plus />
                Add TODO
            </AddButton>
        );
    }

    return (
        <QuickAddContainer>
            <QuickAddRow>
                <Plus />
                <QuickAddInput
                    ref={titleInputRef}
                    type="text"
                    placeholder="What needs to be done?"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSubmitting}
                />
                {!title.trim() && (
                    <QuickAddHint>
                        Enter
                        <CornerDownLeft size={12} />
                        to add
                    </QuickAddHint>
                )}
                {title.trim() && !showDetails && (
                    <QuickAddHint>
                        Enter
                        <CornerDownLeft size={12} />
                        to add
                        &middot;
                        Tab for options
                    </QuickAddHint>
                )}
            </QuickAddRow>

            {(showDetails || hasDetails) && (
                <DetailsSection>
                    <DetailsFieldsRow>
                        <CreateFormPrioritySelect
                            value={priority}
                            onChange={(e) => setPriority(e.target.value as TodoPriority)}
                            onKeyDown={handleDetailsKeyDown}
                            aria-label="Priority"
                        >
                            <option value="">Priority</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                        </CreateFormPrioritySelect>
                        <StyledDateTimePicker
                            value={dueDate ? new Date(dueDate) : undefined}
                            onChange={(value: Date) => {
                                if (!value) {
                                    setDueDate('');
                                    return;
                                }
                                const year = value.getFullYear();
                                const month = String(value.getMonth() + 1).padStart(2, '0');
                                const day = String(value.getDate()).padStart(2, '0');
                                const hours = String(value.getHours()).padStart(2, '0');
                                const minutes = String(value.getMinutes()).padStart(2, '0');
                                setDueDate(`${year}-${month}-${day}T${hours}:${minutes}`);
                            }}
                            placeholder="Due date"
                            hideLabel
                            size="small"
                            borderRadius="semi-rounded"
                        />
                    </DetailsFieldsRow>
                    <StyledAssigneeWrapper>
                        <TeamMemberPicker
                            agentId={agentId}
                            value={assignedTo || undefined}
                            placeholder="Assign to..."
                            onChange={(value) => setAssignedTo(value ?? null)}
                            hideLabel
                        />
                    </StyledAssigneeWrapper>
                    <CreateFormTextarea
                        placeholder="Add details (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyDown={handleDetailsKeyDown}
                        rows={2}
                    />
                </DetailsSection>
            )}

            <QuickAddActions>
                <CreateAnotherLabel>
                    <input
                        type="checkbox"
                        checked={createAnother}
                        onChange={(e) => setCreateAnother(e.target.checked)}
                    />
                    Create another
                </CreateAnotherLabel>
                {!showDetails && !hasDetails && (
                    <DetailsToggle
                        type="button"
                        onClick={() => setShowDetails(true)}
                    >
                        Options
                        <ChevronRight />
                    </DetailsToggle>
                )}
                {(showDetails || hasDetails) && (
                    <DetailsToggle
                        type="button"
                        onClick={() => setShowDetails(false)}
                    >
                        Options
                        <ChevronDown />
                    </DetailsToggle>
                )}
                <CreateFormButton
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </CreateFormButton>
                <CreateFormButton
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!title.trim() || isSubmitting}
                >
                    {isSubmitting ? 'Creating...' : 'Create'}
                </CreateFormButton>
            </QuickAddActions>
        </QuickAddContainer>
    );
};

export { getDateInputValue };

export default TodoCreateForm;
